/**
 * Version gate — pure logic for the two-tier update gate (Workstream V).
 *
 *   'soft'  → the existing dismissible UpdateBanner. Suggests.
 *   'hard'  → ForceUpdateGate: a blocking wall, plus Google's native immediate
 *             update flow on Android. Enforces.
 *
 * Two Remote Config floors drive it, and they are deliberately SEPARATE keys
 * rather than a major/minor rule derived from the version number:
 *
 *   min_ios_version / min_android_version          → soft floor (banner)
 *   force_update_below_ios / _android              → hard floor (wall)
 *
 * Convention is to set the hard floor at a major boundary (e.g. `2.0.0`), but
 * enforcement stays an explicit decision. Deriving it from the version number
 * would mean we could never hard-gate a patch (a security fix we cannot leave
 * in the wild) without a fake major bump, and could never ship a 2.0.0 without
 * walling everyone whether we meant to or not.
 *
 * FAILURE LADDER — read before changing any of this.
 *
 * The old gate failed OPEN on malformed input: `NaN` comparisons silently
 * returned false and no banner ever showed. We do not want that back. But the
 * naive correction — "fail closed" — is catastrophic for a *wall*: a typo'd
 * floor in the Remote Config console would brick every install with a screen
 * they cannot dismiss, and we would have no way to reach them except another
 * console edit. So the tiers fail in different directions, on purpose:
 *
 *   - Unparseable CURRENT version  → 'soft'. Degrade to a banner: visible to
 *     us, harmless to them. Never escalate an unknown into a wall.
 *   - Unparseable FLOOR            → ignore THAT floor, and keep evaluating the
 *     other one on its own merits. An ops typo must not be able to wall anyone,
 *     and must not banner the whole fleet either: a garbage `force_update_below`
 *     leaves clients above the soft floor at 'none'.
 *   - Wall additionally requires `updateAvailableInStore`. This is the real
 *     safety, and it is stronger than any comparator semantics: if we raise
 *     the floor to 2.0.0 before Apple approves 2.0.0, nobody gets walled off
 *     behind a button that does nothing.
 */

export type GateTier = 'none' | 'soft' | 'hard';

/** Parse a semver-ish string to numeric segments. `null` when unusable. */
export function parseVersion(version: string | undefined | null): number[] | null {
  if (!version) return null;
  // Drop a semver pre-release/build tag before splitting: the Android dev
  // flavor ships versionName `1.42.7-dev` (build.gradle versionNameSuffix),
  // and rejecting it left every dev install permanently on the soft banner
  // via the unparseable-current degrade. `1.42.7-dev` compares as `1.42.7`.
  const core = version.trim().split(/[-+]/, 1)[0];
  const segments = core.split('.');
  if (segments.length === 0) return null;

  const parsed = segments.map((segment) => {
    // Reject anything that isn't purely digits — `v1`, ``, ` `.
    // Number('') is 0 and Number(' ') is 0, so a digit test is required.
    if (!/^\d+$/.test(segment)) return NaN;
    return Number(segment);
  });

  if (parsed.some(Number.isNaN)) return null;
  // Pad to major.minor.patch so `1.30` and `1.30.0` compare equal.
  while (parsed.length < 3) parsed.push(0);
  return parsed.slice(0, 3);
}

/**
 * Is `current` strictly below `floor`?
 * `null` when either side is unparseable — the caller decides what that means.
 */
export function isBelow(current: string, floor: string): boolean | null {
  const cur = parseVersion(current);
  const min = parseVersion(floor);
  if (!cur || !min) return null;

  for (let i = 0; i < 3; i++) {
    if (cur[i] < min[i]) return true;
    if (cur[i] > min[i]) return false;
  }
  return false; // equal
}

/**
 * THE WEB STORY (V3) — web has no store to send anyone to, so its "hard gate"
 * is a FORCED RELOAD: the fix is always one reload away (Hosting serves the new
 * bundle). The floor rides in version.json itself (`forceReloadBelow`, set in
 * apps/enough/version-floors.json and baked at build) — deliberately NOT a
 * Remote Config key: long-lived web tabs never refetch RC, but they already
 * poll version.json every 5 minutes, so the floor arrives on the same channel
 * as the deploy signal it gates on.
 *
 * The failure ladder extends to web with two reload-specific safeties:
 *
 *   - `reload` additionally requires a DEPLOYED buildHash different from the
 *     running one — the web analogue of `updateAvailableInStore`. A floor set
 *     above the deployed build itself (ops typo) must not reload anyone: the
 *     reload would land on the same bundle and loop forever.
 *   - One attempt per target build (`alreadyAttemptedFor`): if we forced a
 *     reload for deploy X and are somehow still below the floor, degrade to
 *     the banner instead of reloading again. No input can loop the client.
 *
 * WHEN to reload is not decided here — the scheduler (lib/webForceReload.ts)
 * defers while a checkout/Plaid surface is live so nobody is trapped mid-payment.
 */
export type WebGateDecision = 'none' | 'soft' | 'reload';

export interface WebGateInput {
  /** The running JS bundle's version (APP_VERSION — baked at build). */
  currentVersion: string;
  /** `forceReloadBelow` from the deployed version.json. Empty/absent = no floor. */
  floor?: string;
  /** `buildHash` from the deployed version.json. */
  deployedBuildHash?: string;
  /** BUILD_HASH baked into the running bundle. */
  runningBuildHash: string;
  /** The deployed hash we last forced a reload for (localStorage), if any. */
  alreadyAttemptedFor?: string | null;
}

export function resolveWebGate({
  currentVersion,
  floor,
  deployedBuildHash,
  runningBuildHash,
  alreadyAttemptedFor,
}: WebGateInput): WebGateDecision {
  const newDeploy = !!deployedBuildHash && deployedBuildHash !== runningBuildHash;
  const below = floor ? isBelow(currentVersion, floor) : false;

  // Unparseable comparison: currentVersion is our own build constant, so in
  // practice this means a typo'd floor — ignore it (an ops typo must not be
  // able to force-reload anyone), leaving the ordinary deploy-signal banner.
  if (below === null) return newDeploy ? 'soft' : 'none';

  // No newer deploy to land on → never reload (see safety #1 above). Below a
  // floor with nothing to install still nudges, mirroring the native ladder.
  if (!newDeploy) return below ? 'soft' : 'none';

  if (!below) return 'soft';
  if (alreadyAttemptedFor === deployedBuildHash) return 'soft';
  return 'reload';
}

export interface GateInput {
  /** The NATIVE app version (Info.plist / build.gradle), never the JS bundle's. */
  currentVersion: string;
  /** RC soft floor. Empty/absent = no soft floor. */
  softFloor?: string;
  /** RC hard floor. Empty/absent = no hard floor. */
  hardFloor?: string;
  /**
   * Does the store actually have a newer build to install? The wall is never
   * shown without this — see the failure ladder above.
   */
  updateAvailableInStore: boolean;
}

export function resolveGateTier({
  currentVersion,
  softFloor,
  hardFloor,
  updateAvailableInStore,
}: GateInput): GateTier {
  // Undecidable CURRENT version: degrade to a banner, never escalate to a wall.
  // Tested first and on its own so this rung cannot be confused with a typo'd
  // floor — a bad `currentVersion` is our bug and should stay visible to us.
  if (!parseVersion(currentVersion)) {
    return hardFloor || softFloor ? 'soft' : 'none';
  }

  // Current parses, so a `null` from isBelow() below can only mean an
  // unparseable FLOOR. Treat that floor as not-crossed — ignoring only itself,
  // while the other floor is still evaluated. One typo in the Remote Config
  // console must not show the banner to every user (including the up-to-date).
  const belowHard = hardFloor ? isBelow(currentVersion, hardFloor) === true : false;
  const belowSoft = softFloor ? isBelow(currentVersion, softFloor) === true : false;

  if (belowHard && updateAvailableInStore) return 'hard';
  // Below the hard floor but nothing to install yet — still worth nudging.
  if (belowHard || belowSoft) return 'soft';
  return 'none';
}
